import React, { useEffect, useState } from 'react';
import { SigekoRun } from './SigekoRun';
import { PIXEL_FONT } from './runShared';

/**
 * Wartungsseite "Bauphase": ersetzt das ganze Layout, solange ein Update
 * eingebaut wird. Keine Navigation der App — nur Ansage, Uhr, das Spiel und
 * (fuer Admins) zwei dezente Knoepfe.
 */
export interface BauphaseScreenProps {
    /** Ansagetext des Admins, leer erlaubt. */
    message: string;
    /** Beginn der Sperre (locks_at) fuer "seit mm:ss". */
    sinceMs: number | null;
    /** Notausgang fuer "zurueck spaetestens HH:MM". */
    expiresAtMs: number | null;
    /** z. B. "Eine Aenderung liegt noch auf diesem Geraet …" */
    queuedHint?: string | null;
    /** Admin darf hinein. */
    adminBypass?: boolean;
    /** Knopf "Umgehen" (nur bei adminBypass): Admin geht zum Pruefen hinein. */
    onAdminEnter?: () => void;
    /** Knopf "Bauphase beenden" (nur bei adminBypass). */
    onEndMaintenance?: () => void;
}

/** Logo aus dem Storage — dieselbe Adresse wie in der Sidebar. */
const LOGO_URL = 'https://otkrfgysfhohefploofg.supabase.co/storage/v1/object/public/static-assets/GefAS_Logo.png';

const pad2 = (n: number): string => String(n).padStart(2, '0');

function formatSince(sinceMs: number, nowMs: number): string {
    const s = Math.max(0, Math.floor((nowMs - sinceMs) / 1000));
    return pad2(Math.floor(s / 60)) + ':' + pad2(s % 60);
}

function formatUntil(expiresAtMs: number | null): string {
    if (expiresAtMs === null) return '--:--';
    const d = new Date(expiresAtMs);
    return pad2(d.getHours()) + ':' + pad2(d.getMinutes());
}

export const BauphaseScreen: React.FC<BauphaseScreenProps> = ({
    message,
    sinceMs,
    expiresAtMs,
    queuedHint,
    adminBypass = false,
    onAdminEnter,
    onEndMaintenance,
}) => {
    const [night, setNight] = useState(false);
    const [sound, setSound] = useState(false);
    // Ohne locks_at zaehlt die Uhr ab dem Aufruf der Seite (wie der Prototyp).
    const [mountedAt] = useState(() => Date.now());
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const bg = night ? '#1B1F26' : '#F7F7F5';
    const ink = night ? '#E6E9EE' : '#1B1F26';
    const muted = night ? '#97A1AE' : '#6B7280';
    const orange = '#E8640C';
    const px: React.CSSProperties = { fontFamily: PIXEL_FONT, letterSpacing: '0.02em' };

    return (
        <div
            className="fixed inset-0 z-[1000] overflow-y-auto flex flex-col items-center justify-center gap-[18px] p-6"
            style={{ background: bg, color: ink, transition: 'background .4s, color .4s', fontFamily: '"IBM Plex Sans", "Inter", system-ui, sans-serif' }}
            role="main"
            aria-label="Bauphase"
        >
            <div className="text-center grid gap-2" style={px}>
                <img
                    src={LOGO_URL}
                    alt="GefAS Arbeitssicherheit"
                    className="h-11 w-auto block mx-auto mb-1.5"
                    style={night ? { filter: 'brightness(0) invert(1)' } : undefined}
                />
                <div className="text-[.72rem]" style={{ color: muted }}>BAUPHASE · UPDATE WIRD EINGEBAUT</div>
                <h1 className="text-[1.15rem] m-0 leading-[1.6] font-normal">WIR BAUEN UM. SOLANGE: LAUF.</h1>
                <div className="text-[.7rem]" style={{ color: muted }}>
                    SEIT <b className="font-normal" style={{ color: orange }}>{formatSince(sinceMs ?? mountedAt, now)}</b>
                    {' · '}
                    ZURÜCK SPÄTESTENS <b className="font-normal" style={{ color: orange }}>{formatUntil(expiresAtMs)}</b>
                </div>
            </div>

            {message.trim() !== '' && (
                <p className="text-[.9rem] text-center max-w-[56ch] leading-relaxed m-0 whitespace-pre-line" style={{ color: ink }}>
                    {message}
                </p>
            )}

            <div className="w-full max-w-[900px]">
                <SigekoRun night={night} onNightChange={setNight} sound={sound} />
            </div>

            <div className="text-[.7rem] text-center flex gap-[18px] justify-center flex-wrap items-center" style={{ ...px, color: muted }}>
                <span>LEERTASTE / TIPPEN: SPRINGEN</span>
                <span>PFEIL RUNTER: DUCKEN</span>
                <button
                    type="button"
                    onClick={() => setSound(s => !s)}
                    className="bg-transparent border border-current px-2 py-1 cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2"
                    style={{ font: 'inherit', color: 'inherit', outlineColor: orange }}
                    aria-pressed={sound}
                >
                    TON: {sound ? 'AN' : 'AUS'}
                </button>
            </div>

            <p className="text-[.8rem] text-center max-w-[56ch] leading-normal m-0" style={{ color: muted }}>
                Ihre Eingaben sind gesichert. Die Seite verschwindet von selbst, wenn die Bauphase endet.{' '}
                <b className="font-medium" style={{ color: ink }}>Rekord bleibt auf diesem Gerät.</b>
            </p>

            {queuedHint && (
                <p className="text-[.8rem] text-center max-w-[56ch] leading-normal m-0" style={{ color: orange }}>
                    {queuedHint}
                </p>
            )}

            {adminBypass && (
                <div className="flex gap-3 flex-wrap justify-center mt-2">
                    {onAdminEnter && (
                        <button
                            type="button"
                            onClick={onAdminEnter}
                            className="text-xs px-3 py-1.5 border border-current rounded bg-transparent cursor-pointer opacity-70 hover:opacity-100"
                            style={{ color: muted }}
                        >
                            Umgehen
                        </button>
                    )}
                    {onEndMaintenance && (
                        <button
                            type="button"
                            onClick={onEndMaintenance}
                            className="text-xs px-3 py-1.5 border border-current rounded bg-transparent cursor-pointer opacity-70 hover:opacity-100"
                            style={{ color: muted }}
                        >
                            Bauphase beenden
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default BauphaseScreen;
